import MojitoServices from 'mojito/services';
import MojitoCore from 'mojito/core';
import { useDispatch, useSelector } from 'react-redux';
import { difference, isString, pick } from 'mojito/utils';
import MojitoPresentation from 'mojito/presentation';
import { useEffect, useMemo, useState } from 'react';

const { StringUtils } = MojitoCore.Base;
const { uiStyle } = MojitoCore.Presentation;
const { Image, DotNotification, Notification, Button, AbsolutePane, GridLayout, Text, FlexPane } =
    MojitoPresentation.Components;
const { GridPane } = GridLayout;
const { selectBetCount } = MojitoServices.Bets.openBetsSelectors;
const { selectLiveEventCount } = MojitoServices.MetaInformation.selectors;
const intentActions = MojitoCore.Intents.actions;

// Minimum scroll delta, in pixels, required before a scroll is considered a direction change.
const SCROLL_DIRECTION_THRESHOLD_PX = 4;
// Distance scrolled from the top, in pixels, before the container is allowed to hide.
// Keeps the container visible while the user is still near the top of the page.
const HIDE_ON_SCROLL_OFFSET_PX = 60;

export default function QuickLinks(props) {
    const {
        appContext: { analyticsEmitter },
    } = props.mojitoTools;
    const betCount = useSelector(state => selectBetCount(state));
    const dispatch = useDispatch();

    const onLinkClick = (event, data) => {
        const { intent, label } = data;
        if (intent.type) {
            analyticsEmitter.emitAnalytics('quickLinkClicked', label);
            const parsedIntentData = isString(intent.data) ? JSON.parse(intent.data) : intent.data;
            dispatch(intentActions.publishIntent(intent.type, parsedIntentData));
        }
    };

    const notifications = useMemo(() => ({ hasOpenBets: betCount > 0 }), [betCount]);

    return <QuickLinksView {...props} notifications={notifications} onLinkClick={onLinkClick} />;
}

const QuickLinksView = props => {
    const {
        mojitoTools: { config, style },
    } = props;
    const isHidden = useHideOnScroll(config.hideOnScroll);
    const containerConfig = isHidden ? style.hiddenContainer : config.container;

    const links = config.links || [];
    const rightLinks = links.filter(link => link.isRightAligned);
    const leftLinks = difference(links, rightLinks);
    const hasRightLinks = rightLinks.length > 0;

    // we need to render dummyContainer and include it to normal layout flow to fix scrolling issue
    // that will happen if this component will be placed inside scrollable area.
    // Due to the `position:fixed` the container will be moved out from normal flow layout,
    // that's why there will be no possible to scroll content to the end. Dummy container fixes this
    // preserving space at the bottom of the container.
    return (
        <>
            {!config.hideOnScroll && <div style={style.dummyContainer} />}
            <AbsolutePane config={containerConfig} class={'ta-QuickLinks'}>
                {hasRightLinks ? (
                    <FlexPane config={config.groupsContainer} class={'ta-groupsContainer'}>
                        <GridPane config={config.linksContainer}>
                            <LinksList {...props} groupLinks={leftLinks} />
                        </GridPane>
                        <GridPane config={config.rightLinksContainer}>
                            <LinksList {...props} groupLinks={rightLinks} />
                        </GridPane>
                    </FlexPane>
                ) : (
                    <GridPane config={config.linksContainer} class={'ta-linksContainer'}>
                        <LinksList {...props} groupLinks={links} />
                    </GridPane>
                )}
            </AbsolutePane>
        </>
    );
};

const LinksList = props => {
    const { groupLinks, notifications, onLinkClick } = props;

    return groupLinks.map(link => (
        <Link
            {...props}
            key={link.label}
            notifications={notifications}
            link={link}
            onClick={onLinkClick}
        />
    ));
};

const Link = props => {
    const {
        link,
        onClick,
        notifications,
        mojitoTools: { config, stringResolver },
    } = props;
    const notificationActions = link.notification?.onActions;
    const hasNotification = notificationActions?.some(action => notifications[action] === true);
    const isDotNotificationVisible = config.isDotNotificationVisible && hasNotification;
    const intent = {
        type: link.intent && link.intent.type,
        data: link.intent && JSON.stringify(link.intent.data),
    };
    const data = { intent, label: StringUtils.deriveLabelNamePrefix(link.label) };
    const label = stringResolver.resolveString(link.label);
    const linkStyle = useLinkConfig(link, config);
    const liveEventCount = useSelector(state => selectLiveEventCount(state));

    return (
        <Button
            config={linkStyle.linkButton}
            onClickData={data}
            onClick={onClick}
            hrefLink={link.hrefLink}
            class={StringUtils.makeCssClassName('ta', link.label)}
            key={label}
        >
            <AbsolutePane config={linkStyle.linkDecorationContainer} />
            <FlexPane config={linkStyle.linkContainer}>
                {isDotNotificationVisible ? (
                    <DotNotification
                        config={linkStyle.dotNotification}
                        isVisible={isDotNotificationVisible}
                    >
                        <Image src={link.icon} config={linkStyle.linkIcon} />
                    </DotNotification>
                ) : (
                    <Image src={link.icon} config={linkStyle.linkIcon} />
                )}
                {link.showLiveCount && liveEventCount > 0 && (
                    <AbsolutePane config={linkStyle.liveCountBadgeContainer}>
                        <Notification
                            text={String(liveEventCount)}
                            config={linkStyle.liveCountBadge}
                            class="ta-QuickLinks-LiveCountBadge"
                        />
                    </AbsolutePane>
                )}
                {config.showLabel && <Text config={linkStyle.linkLabel}>{label}</Text>}
            </FlexPane>
        </Button>
    );
};

const useLinkConfig = (link, config) => {
    return useMemo(() => {
        const mergeLinkStyle = prop =>
            link[prop] ? uiStyle.merge(config[prop], link[prop]) : config[prop];
        const linkContainer = mergeLinkStyle('linkContainer');
        const linkButton = mergeLinkStyle('linkButton');
        const linkLabel = mergeLinkStyle('linkLabel');
        const linkIcon = mergeLinkStyle('linkIcon');
        const dotNotification = mergeLinkStyle('dotNotification');
        const linkDecorationContainer = mergeLinkStyle('linkDecorationContainer');
        const liveCountBadgeContainer = mergeLinkStyle('liveCountBadgeContainer');
        const liveCountBadge = mergeLinkStyle('liveCountBadge');
        return {
            linkContainer,
            linkButton,
            linkLabel,
            linkIcon,
            dotNotification,
            linkDecorationContainer,
            liveCountBadgeContainer,
            liveCountBadge,
        };
    }, [config, link]);
};

const useHideOnScroll = isEnabled => {
    const [isHidden, setIsHidden] = useState(false);

    useEffect(() => {
        if (!isEnabled) {
            return;
        }

        let lastScrollY = window.scrollY;

        const onScroll = () => {
            const currentScrollY = window.scrollY;
            const scrollDelta = currentScrollY - lastScrollY;
            if (Math.abs(scrollDelta) < SCROLL_DIRECTION_THRESHOLD_PX) {
                return;
            }

            const isScrollingDown = scrollDelta > 0;
            setIsHidden(isScrollingDown && currentScrollY > HIDE_ON_SCROLL_OFFSET_PX);
            lastScrollY = currentScrollY;
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [isEnabled]);

    return isEnabled && isHidden;
};

QuickLinks.getStyle = (config, mode, merge) => {
    const { container, hiddenContainer, hideOnScroll } = config;
    return {
        dummyContainer: hideOnScroll
            ? undefined
            : pick(container.style, 'width', 'height', 'left', 'right', 'top', 'bottom'),
        hiddenContainer: merge(container, hiddenContainer),
    };
};
